import { config } from '@/config'
import type { Incident, IncidentListing, Track, WorkOrder } from './types'
import {
  fetchIncidentTask,
  fetchIncidentTasks,
  fetchTrack,
  fetchWorkOrdersFor,
  PropertyCache,
} from './cotalker'
import {
  incidentPropertyIds,
  toIncident,
  toWorkOrder,
  workOrderPropertyCodes,
  workOrderPropertyIds,
} from '@/domain/map'
import { LIST_EXCLUDED_INCIDENTS, LIST_EXCLUDED_WORK_ORDERS } from '@/domain/track'
import { mockLoadIncidents, mockLoadWorkOrders, mockReloadIncident, mockTracks } from './mock'
import { hasToken } from '@/auth/session'

export interface Tracks {
  incidents: Track
  workOrders: Track
}

export interface Repository {
  loadTracks(signal?: AbortSignal): Promise<Tracks>
  loadIncidents(signal?: AbortSignal): Promise<IncidentListing>
  loadWorkOrders(incident: Incident, signal?: AbortSignal): Promise<WorkOrder[]>
  /**
   * Relee una incidencia. `null` significa que dejó de pertenecer al listado
   * —se cerró o se rechazó mientras el portal estaba abierto—, no que falló:
   * un fallo lanza.
   */
  reloadIncident(incidentId: string, signal?: AbortSignal): Promise<Incident | null>
}

/** Un estado excluido del listado no se dibuja ni se cuenta. */
const isListed = (incident: Incident): boolean =>
  !incident.state.code || !LIST_EXCLUDED_INCIDENTS.has(incident.state.code)

/**
 * Tope del listado. Se carga una sola vez, sin scroll que pida más: son las
 * `LIST_LIMIT` incidencias listables más recientes, que después se agrupan y
 * se ordenan por prioridad (domain/grouping.ts).
 */
export const LIST_LIMIT = 100

/**
 * Cuántas páginas crudas se piden como máximo para llenar el listado.
 *
 * En la medición del 14-ago eran 4 estados excluidos en las primeras 50, así
 * que una ronda basta casi siempre. El techo existe para que una estación con
 * la historia entera cerrada no dispare una cascada de llamadas al abrir.
 * Era 5 cuando la página cruda era de 21; ahora es de 100 más su lote de
 * propiedades, así que 3 rondas ya revisan 300 tareas.
 */
const MAX_LIST_ROUNDS = 3

export interface RawPageOf<T> {
  listed: T[]
  cursor: string | null
  hasMore: boolean
}

/**
 * Junta páginas crudas hasta llenar el listado del portal.
 *
 * Hace falta porque el filtro por estado corre en el cliente: v2 lo acepta y lo
 * ignora (ver `fetchIncidentTasks`), así que una página cruda rinde menos
 * elementos visibles de los que pide `size` — y rinde cero si toda ella está
 * cerrada. Sin insistir, una estación con historia mayormente cerrada abriría
 * el portal en blanco aunque tenga incidencias abiertas más atrás: otra vez el
 * modo de falla que este portal ya pagó caro, lista vacía y ningún error.
 *
 * Dos invariantes que las pruebas fijan:
 *
 * - **El cursor avanza sobre la lista CRUDA.** La página siguiente arranca
 *   donde terminó lo que se PIDIÓ, no donde terminó lo que se muestra; si
 *   saliera de la filtrada, la siguiente se saltaría elementos.
 * - **No se recorta a `size`.** Devolver menos de lo consumido dejaría el
 *   cursor por delante de lo visible, con el mismo salto.
 */
export async function collectPages<T>(
  size: number,
  start: string | null,
  loadPage: (cursor: string | null) => Promise<RawPageOf<T>>,
  maxRounds = MAX_LIST_ROUNDS,
): Promise<{ items: T[]; cursor: string | null; hasMore: boolean }> {
  const items: T[] = []
  let cursor = start
  let hasMore = true

  for (let round = 0; round < maxRounds && items.length < size && hasMore; round++) {
    const page = await loadPage(cursor)
    items.push(...page.listed)
    cursor = page.cursor
    hasMore = page.hasMore
  }

  return { items, cursor, hasMore }
}

/**
 * Recorta lo que juntó `collectPages` al tope del listado.
 *
 * `collectPages` no recorta a propósito, para no dejar el cursor por delante
 * de lo visible. Acá ya no hay ronda siguiente que dependa del cursor, así que
 * recortar es seguro — y se recorta ANTES de agrupar y ordenar por prioridad:
 * las que quedan son las más recientes, no las más urgentes.
 *
 * `truncated` avisa que puede haber incidencias listables más antiguas que no
 * entraron, sea porque sobraron o porque se agotaron las rondas antes de ver
 * toda la historia. Sin eso, un listado cortado se vería igual que uno
 * completo.
 */
export function capListing<T>(
  items: T[],
  hasMore: boolean,
  limit: number,
): { items: T[]; truncated: boolean } {
  return { items: items.slice(0, limit), truncated: hasMore || items.length > limit }
}

/* ---------- Contra la API real ---------- */

const cache = new PropertyCache()
let tracksPromise: Promise<Tracks> | null = null

const live: Repository = {
  loadTracks(signal) {
    // El catálogo se pide una sola vez por sesión: no cambia mientras el
    // portal está abierto y las dos llamadas se pagan al arrancar.
    tracksPromise ??= Promise.all([
      fetchTrack(config.TASK_GROUP_INCIDENTS, 'inc', cache, signal),
      fetchTrack(config.TASK_GROUP_WORK_ORDERS, 'ot', cache, signal),
    ])
      .then(([incidents, workOrders]) => ({ incidents, workOrders }))
      .catch((err) => {
        tracksPromise = null // que un fallo de red no deje el catálogo envenenado
        throw err
      })
    return tracksPromise
  },

  async loadIncidents(signal) {
    const tracks = await this.loadTracks(signal)

    const page = await collectPages(LIST_LIMIT, null, async (from) => {
      const raw = await fetchIncidentTasks(from, signal)

      // Todos los nombres de la página en una sola llamada.
      await cache.ensure(raw.tasks.flatMap(incidentPropertyIds), signal)
      const props = cache.asMap()

      const listed = raw.tasks
        .map((task) => toIncident(task, tracks.incidents, props))
        .filter(isListed)

      return { listed, cursor: raw.cursor, hasMore: raw.hasMore }
    })

    const { items, truncated } = capListing(page.items, page.hasMore, LIST_LIMIT)
    return { incidents: items, truncated }
  },

  async reloadIncident(incidentId, signal) {
    const tracks = await this.loadTracks(signal)
    const task = await fetchIncidentTask(incidentId, signal)
    if (!task) return null

    await cache.ensure(incidentPropertyIds(task), signal)
    const incident = toIncident(task, tracks.incidents, cache.asMap())
    return isListed(incident) ? incident : null
  },

  async loadWorkOrders(incident, signal) {
    if (incident.otPropertyIds.length === 0) return []
    const tracks = await this.loadTracks(signal)

    // Se manda el id de la INCIDENCIA. El backend lo valida con el token de
    // este usuario antes de resolver nada; los ids de orden no salen de acá.
    const sources = await fetchWorkOrdersFor(incident._id, signal)

    await cache.ensure(
      sources.flatMap(({ task, property }) => workOrderPropertyIds(task, property)),
      signal,
    )
    // El proveedor puede venir como código en vez de id; se resuelve aparte.
    await cache.ensureCodes(
      sources.flatMap(({ task, property }) => workOrderPropertyCodes(task, property)),
      signal,
    )
    const props = cache.asMap()

    return sources
      .map(({ task, property, userCanOpen }) =>
        toWorkOrder(task, property, tracks.workOrders, props, cache.codeMap(), userCanOpen),
      )
      .filter((ot) => !ot.state.code || !LIST_EXCLUDED_WORK_ORDERS.has(ot.state.code))
  },
}

/* ---------- Con fixtures ---------- */

const mock: Repository = {
  async loadTracks() {
    return mockTracks
  },
  async loadIncidents() {
    // Los fixtures devuelven toda su historia listable; el tope se aplica acá,
    // con la misma función que la ruta real.
    const { items, truncated } = capListing(await mockLoadIncidents(), false, LIST_LIMIT)
    return { incidents: items, truncated }
  },
  loadWorkOrders: (incident) => mockLoadWorkOrders(incident),
  reloadIncident: (incidentId) => mockReloadIncident(incidentId),
}

/**
 * Un token real gana sobre `USE_MOCKS`.
 *
 * Si alguien abre el portal con `?cotalker-token=…` sobre una configuración
 * que trae los fixtures encendidos, lo que espera ver son sus datos, no los de
 * muestra. La decisión se toma en la primera consulta y no al importar el
 * módulo, porque `initSession()` corre después de que se resuelven los imports
 * y antes de eso todavía no se sabe si hay token.
 */
let chosen: Repository | null = null
function impl(): Repository {
  chosen ??= config.USE_MOCKS && !hasToken() ? mock : live
  return chosen
}

export const isUsingMocks = (): boolean => impl() === mock

export const repository: Repository = {
  loadTracks: (signal) => impl().loadTracks(signal),
  loadIncidents: (signal) => impl().loadIncidents(signal),
  loadWorkOrders: (incident, signal) => impl().loadWorkOrders(incident, signal),
  reloadIncident: (incidentId, signal) => impl().reloadIncident(incidentId, signal),
}
